import { useState, useEffect, useMemo } from 'react'
import {
    Box,
    Card,
    Grid,
    Toolbar,
    IconButton,
    Paper,
    Button,
    Typography,
    TextField,
    Chip,
    Divider,
    InputAdornment,
    Switch,
    FormControlLabel,
    CircularProgress,
    List,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Tooltip
} from '@mui/material'
import {
    ArrowBackIos,
    AddCircleOutline,
    Edit as EditIcon,
    Search as SearchIcon,
    Shield as ShieldIcon,
    CheckCircle,
    Cancel,
    Lock,
    Refresh
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { rolesRoutes, permissionsRoutes } from '../../routes'
import CreateRoleDialog from '../../dialogs/CreateRoleDialog'
import EditRoleDialog from '../../dialogs/EditRoleDialog'
import { GenerationStatusModal } from '../../dialogs/StatusModal'
import { useUser, useStatus } from '../../context'
import './style.css'

const groupLabelMap = {
    CREATE: 'Creación',
    VIEW: 'Visualización',
    EDIT: 'Edición',
    DELETE: 'Eliminación',
    SYNC: 'Sincronización',
    START: 'Ejecución',
    UPDATE: 'Actualización',
    COMPLETE: 'Finalización',
    MANAGE: 'Administración',
    GENERATE: 'Generación PDF'
}

const RolesPage = () => {
    const { admin } = useUser()
    const { setstatus } = useStatus()
    const navigate = useNavigate()

    const [roles, setRoles] = useState([])
    const [allPermissions, setAllPermissions] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedRole, setSelectedRole] = useState(null)
    const [onlyAssigned, setOnlyAssigned] = useState(true)

    const [openCreateDialog, setOpenCreateDialog] = useState(false)
    const [openEditDialog, setOpenEditDialog] = useState(false)

    const fetchData = async (silent = false) => {
        if (!silent && roles.length === 0) setLoading(true)
        try {
            const [rolesRes, permsRes] = await Promise.allSettled([
                rolesRoutes.getRoles(),
                permissionsRoutes.getPermissions()
            ])

            let newRoles = []
            if (rolesRes.status === 'fulfilled' && rolesRes.value?.data) {
                newRoles = rolesRes.value.data
                setRoles(newRoles)
            }

            if (permsRes.status === 'fulfilled' && permsRes.value?.data) {
                setAllPermissions(permsRes.value.data)
            }

            // Sync selectedRole with updated data
            if (selectedRole) {
                const updated = newRoles.find(r => r._id === selectedRole._id)
                if (updated) {
                    setSelectedRole(updated)
                }
            } else if (newRoles.length > 0) {
                setSelectedRole(newRoles[0])
            }
        } catch (error) {
            console.error('Error fetching roles and permissions:', error)
            setstatus({
                status: 'error',
                title: 'Error al Cargar Datos',
                message: 'No se pudieron obtener los roles y permisos del sistema.',
                severity: 'error'
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    const handleOpenCreateDialog = () => {
        setOpenCreateDialog(true)
    }

    const handleCloseCreateDialog = async (success = false) => {
        setOpenCreateDialog(false)
        if (success === true) {
            await fetchData(true)
        }
    }

    const handleOpenEditDialog = (role, e) => {
        if (e) e.stopPropagation()
        setSelectedRole(role)
        setOpenEditDialog(true)
    }

    const handleCloseEditDialog = async (success = false) => {
        setOpenEditDialog(false)
        if (success === true) {
            await fetchData(true)
        }
    }

    // Filter roles by search term
    const filteredRoles = useMemo(() => {
        if (!searchTerm.trim()) return roles
        const term = searchTerm.toLowerCase()
        return roles.filter(r =>
            (r.name && r.name.toLowerCase().includes(term)) ||
            (r.dbName && r.dbName.toLowerCase().includes(term)) ||
            (r.description && r.description.toLowerCase().includes(term))
        )
    }, [roles, searchTerm])

    // Set of assigned permission IDs for the selected role
    const assignedPermissionIds = useMemo(() => {
        if (!selectedRole || !Array.isArray(selectedRole.permissions)) return new Set()
        return new Set(selectedRole.permissions.map(p => typeof p === 'object' ? p._id : p))
    }, [selectedRole])

    // Permissions to display on the right panel
    const displayedPermissions = useMemo(() => {
        if (onlyAssigned) {
            return allPermissions.filter(p => assignedPermissionIds.has(p._id))
        }
        return allPermissions
    }, [allPermissions, assignedPermissionIds, onlyAssigned])

    // Group displayed permissions by action key (CREATE, VIEW, EDIT, etc.)
    const groupedPermissions = useMemo(() => {
        return displayedPermissions.reduce((acc, perm) => {
            const key = perm.key || 'OTROS'
            const groupKey = key.split('_')[0] || 'OTROS'
            if (!acc[groupKey]) {
                acc[groupKey] = []
            }
            acc[groupKey].push(perm)
            return acc
        }, {})
    }, [displayedPermissions])

    return (
        <Box height="100%" sx={{ pt: 12, backgroundColor: '#f4f6f8' }}>
            <Card elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e0e0e0', minHeight: 'calc(100vh - 120px)' }}>
                {/* Header Toolbar */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, pb: 1, borderBottom: '1px solid #eee' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate(-1)} size="small">
                            <ArrowBackIos sx={{ fontSize: 16, color: '#333' }} />
                        </IconButton>
                        <ShieldIcon color="primary" sx={{ fontSize: 22 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a202c' }}>
                            Administración / Roles y Permisos
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton onClick={() => fetchData(true)} size="small" title="Refrescar roles">
                            <Refresh sx={{ fontSize: 18 }} />
                        </IconButton>
                        {admin && (
                            <Button
                                variant="contained"
                                startIcon={<AddCircleOutline />}
                                onClick={handleOpenCreateDialog}
                                size="small"
                                sx={{ backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' }, textTransform: 'none', fontWeight: 'bold' }}
                            >
                                Agregar Rol
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* 2-Column Grid Layout */}
                <Grid container spacing={2} sx={{ height: 'calc(100vh - 200px)' }}>
                    {/* Left Column: Roles List */}
                    <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#fafafa' }}>
                            <Box sx={{ mb: 1.5 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Buscar rol por nombre..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ fontSize: 18, color: '#888' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{ backgroundColor: '#fff', borderRadius: 1 }}
                                />
                            </Box>

                            <Typography variant="caption" sx={{ color: '#666', mb: 1, px: 0.5, fontWeight: 'bold', display: 'block' }}>
                                ROLES ({filteredRoles.length})
                            </Typography>

                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : filteredRoles.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        No se encontraron roles.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ overflowY: 'auto', flexGrow: 1, pr: 0.5 }}>
                                    <List disablePadding>
                                        {filteredRoles.map((role) => {
                                            const isSelected = selectedRole?._id === role._id
                                            const permCount = Array.isArray(role.permissions) ? role.permissions.length : 0
                                            return (
                                                <Paper
                                                    key={role._id}
                                                    elevation={isSelected ? 2 : 0}
                                                    sx={{
                                                        mb: 1,
                                                        borderRadius: 1.5,
                                                        border: isSelected ? '1.5px solid #1976d2' : '1px solid #e0e0e0',
                                                        backgroundColor: isSelected ? '#e3f2fd' : '#ffffff',
                                                        transition: 'all 0.15s ease',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            borderColor: '#1976d2',
                                                            backgroundColor: isSelected ? '#e3f2fd' : '#f5fafd'
                                                        }
                                                    }}
                                                    onClick={() => setSelectedRole(role)}
                                                >
                                                    <ListItemButton sx={{ p: 1.2, borderRadius: 1.5 }}>
                                                        <ListItemText
                                                            primary={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3 }}>
                                                                    <Typography variant="subtitle2" sx={{ fontWeight: isSelected ? 'bold' : 'medium', color: isSelected ? '#0d47a1' : '#222' }}>
                                                                        {role.name}
                                                                    </Typography>
                                                                    <Chip
                                                                        label={role.dbName || 'rol'}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color={isSelected ? 'primary' : 'default'}
                                                                        sx={{ height: 18, fontSize: '0.62rem', fontWeight: 'bold' }}
                                                                    />
                                                                </Box>
                                                            }
                                                            secondary={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                                                                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '170px' }}>
                                                                        {role.description || 'Sin descripción'}
                                                                    </Typography>
                                                                    <Chip
                                                                        label={`${permCount} perm`}
                                                                        size="small"
                                                                        sx={{ height: 18, fontSize: '0.62rem', backgroundColor: isSelected ? '#bbdefb' : '#eee', color: isSelected ? '#0d47a1' : '#555', fontWeight: 'bold' }}
                                                                    />
                                                                </Box>
                                                            }
                                                        />
                                                        {admin && (
                                                            <Tooltip title="Editar este rol">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => handleOpenEditDialog(role, e)}
                                                                    sx={{ ml: 0.5, color: isSelected ? '#1976d2' : '#666' }}
                                                                >
                                                                    <EditIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </ListItemButton>
                                                </Paper>
                                            )
                                        })}
                                    </List>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Right Column: Role Permission Details Preview */}
                    <Grid item xs={12} md={8} sx={{ height: '100%' }}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff', overflowY: 'auto' }}>
                            {selectedRole ? (
                                <>
                                    {/* Role Header Info */}
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', pb: 1.5, borderBottom: '1px solid #eee', mb: 1.5 }}>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a202c' }}>
                                                    {selectedRole.name}
                                                </Typography>
                                                <Chip
                                                    label={selectedRole.dbName || 'dbName'}
                                                    size="small"
                                                    color="primary"
                                                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 'bold' }}
                                                />
                                            </Box>
                                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.82rem' }}>
                                                {selectedRole.description || 'Sin descripción registrada para este rol.'}
                                            </Typography>
                                        </Box>
                                        {admin && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                                                onClick={(e) => handleOpenEditDialog(selectedRole, e)}
                                                sx={{ textTransform: 'none', fontWeight: 'bold', fontSize: '0.78rem' }}
                                            >
                                                Editar Permisos
                                            </Button>
                                        )}
                                    </Box>

                                    {/* Filter & Summary Bar */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, px: 1, py: 0.8, backgroundColor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#334155' }}>
                                                Permisos Asignados:
                                            </Typography>
                                            <Chip
                                                label={`${assignedPermissionIds.size} / ${allPermissions.length}`}
                                                size="small"
                                                color="success"
                                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }}
                                            />
                                        </Box>

                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    size="small"
                                                    checked={onlyAssigned}
                                                    onChange={(e) => setOnlyAssigned(e.target.checked)}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Typography variant="caption" sx={{ fontWeight: 'medium', color: '#475569', fontSize: '0.75rem' }}>
                                                    Solo asignados
                                                </Typography>
                                            }
                                        />
                                    </Box>

                                    {/* Grouped Permissions Preview */}
                                    <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 0.5 }}>
                                        {Object.keys(groupedPermissions).length === 0 ? (
                                            <Box sx={{ textAlign: 'center', py: 6, color: '#888' }}>
                                                <Lock sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                                                <Typography variant="body2">
                                                    {onlyAssigned
                                                        ? 'Este rol no tiene ningún permiso asignado.'
                                                        : 'No hay permisos disponibles para mostrar.'}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Grid container spacing={1.5}>
                                                {Object.entries(groupedPermissions).map(([groupKey, perms]) => {
                                                    const groupLabel = groupLabelMap[groupKey] || groupKey
                                                    return (
                                                        <Grid item xs={12} sm={6} key={groupKey}>
                                                            <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.5, backgroundColor: '#fafafa', border: '1px solid #e5e7eb' }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8, pb: 0.5, borderBottom: '1px dashed #cbd5e1' }}>
                                                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.72rem' }}>
                                                                        {groupLabel} ({groupKey})
                                                                    </Typography>
                                                                    <Chip
                                                                        label={perms.length}
                                                                        size="small"
                                                                        sx={{ height: 16, fontSize: '0.58rem', fontWeight: 'bold', backgroundColor: '#e2e8f0' }}
                                                                    />
                                                                </Box>
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                                                    {perms.map((perm) => {
                                                                        const isAssigned = assignedPermissionIds.has(perm._id)
                                                                        return (
                                                                            <Box
                                                                                key={perm._id}
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justify: 'space-between',
                                                                                    p: 0.6,
                                                                                    borderRadius: 1,
                                                                                    backgroundColor: isAssigned ? '#ecfdf5' : '#f8fafc',
                                                                                    border: isAssigned ? '1px solid #a7f3d0' : '1px solid #f1f5f9'
                                                                                }}
                                                                            >
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, overflow: 'hidden' }}>
                                                                                    {isAssigned ? (
                                                                                        <CheckCircle sx={{ fontSize: 15, color: '#10b981', flexShrink: 0 }} />
                                                                                    ) : (
                                                                                        <Cancel sx={{ fontSize: 15, color: '#94a3b8', flexShrink: 0 }} />
                                                                                    )}
                                                                                    <Box sx={{ overflow: 'hidden' }}>
                                                                                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: isAssigned ? '#065f46' : '#64748b', fontSize: '0.7rem', display: 'block', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                                                            {perm.key}
                                                                                        </Typography>
                                                                                        {perm.description && (
                                                                                            <Typography variant="caption" sx={{ color: isAssigned ? '#047857' : '#94a3b8', fontSize: '0.62rem', display: 'block', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                                                                {perm.description}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Box>
                                                                                </Box>
                                                                                <Chip
                                                                                    label={isAssigned ? 'Sí' : 'No'}
                                                                                    size="small"
                                                                                    color={isAssigned ? 'success' : 'default'}
                                                                                    sx={{ height: 16, fontSize: '0.55rem', fontWeight: 'bold', ml: 0.5, flexShrink: 0 }}
                                                                                />
                                                                            </Box>
                                                                        )
                                                                    })}
                                                                </Box>
                                                            </Paper>
                                                        </Grid>
                                                    )
                                                })}
                                            </Grid>
                                        )}
                                    </Box>
                                </>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#888' }}>
                                    <ShieldIcon sx={{ fontSize: 50, color: '#ccc', mb: 1.5 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#555' }}>
                                        Selecciona un Rol
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" align="center" sx={{ maxWidth: 300, mt: 0.5 }}>
                                        Haz clic sobre cualquiera de los roles de la izquierda para explorar y administrar sus permisos asignados.
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Card>

            {/* Dialogs */}
            <CreateRoleDialog
                open={openCreateDialog}
                onClose={handleCloseCreateDialog}
            />

            {selectedRole && (
                <EditRoleDialog
                    open={openEditDialog}
                    onClose={handleCloseEditDialog}
                    role={selectedRole}
                />
            )}

            {/* Notifications Modal */}
            <GenerationStatusModal />
        </Box>
    )
}

export default RolesPage
